import {
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
  type MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import { pipelineStatusDisplayOptions } from '@/features/quality/components/qualityStatusDataHelpers';
import {
  buildStatusOptions,
  suiteRunSummaryStatusOptions,
  testRunSummaryStatusOptions,
} from '@/shared/constants/statusOptions';
import { formatStatusLabel } from '@/ui/primitives';
import type {
  PipelineStatusKey,
  SuiteStatusKey,
  TestStatusKey,
} from '@/features/quality/components/qualityStatusDataHelpers';
import type { BuildStatus } from '@flowqa/shared';

const buildMetricStatusOrder: BuildStatus[] = ['completed', 'failed', 'running', 'queued'];

export const buildSuiteMetricOptions = (): Array<MetricCardOption<SuiteStatusKey>> => {
  const baseOptions = mapStatusOptionsToMetricOptions(
    suiteRunSummaryStatusOptions,
    resolveMetricTone,
  ) as Array<MetricCardOption<SuiteStatusKey>>;
  return [
    ...baseOptions,
    {
      value: 'untested',
      label: formatStatusLabel('untested'),
      tone: resolveMetricTone('untested'),
    },
  ];
};

export const buildTestMetricOptions = (): Array<MetricCardOption<TestStatusKey>> => {
  const baseOptions = mapStatusOptionsToMetricOptions(
    testRunSummaryStatusOptions,
    resolveMetricTone,
  ) as Array<MetricCardOption<TestStatusKey>>;
  return [
    ...baseOptions,
    {
      value: 'untested',
      label: formatStatusLabel('untested'),
      tone: resolveMetricTone('untested'),
    },
  ];
};

export const buildPipelineMetricOptions = (): Array<MetricCardOption<PipelineStatusKey>> => {
  const orderedPipelineStatuses: Array<Exclude<PipelineStatusKey, 'none'>> = [
    'success',
    'failed',
    'running',
    'queued',
    ...pipelineStatusDisplayOptions
      .filter(
        (option): option is { value: Exclude<PipelineStatusKey, 'none'>; label: string } =>
          option.value !== 'none',
      )
      .map((option) => option.value)
      .filter(
        (value, index, values) =>
          !['success', 'failed', 'running', 'queued'].includes(value) &&
          values.indexOf(value) === index,
      ),
  ];
  const orderedPipelineOptions: Array<{
    value: Exclude<PipelineStatusKey, 'none'>;
    label: string;
  }> = orderedPipelineStatuses.map((value) => {
    const option = pipelineStatusDisplayOptions.find(
      (candidate): candidate is { value: Exclude<PipelineStatusKey, 'none'>; label: string } =>
        candidate.value === value,
    );
    if (!option) {
      return {
        value,
        label: formatStatusLabel(value),
      };
    }
    return option;
  });
  const baseOptions = mapStatusOptionsToMetricOptions(
    orderedPipelineOptions,
    resolveMetricTone,
  ) as Array<MetricCardOption<PipelineStatusKey>>;
  return baseOptions;
};

export const buildBuildMetricOptions = (): Array<MetricCardOption<BuildStatus>> =>
  mapStatusOptionsToMetricOptions(
    buildMetricStatusOrder.map((value) => {
      const option = buildStatusOptions.find((candidate) => candidate.value === value);
      return {
        value,
        label: option?.label ?? formatStatusLabel(value),
      };
    }),
    resolveMetricTone,
  );
